import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Radar, ScanLine, LineChart, Users2, FileText, Check, Minus, Plus, ShieldCheck, Cpu, Database, Zap, Search as SearchIcon, ChevronRight, PlayCircle } from "lucide-react";
import Logo from "@/components/Logo";
import LandingDemo from "@/components/LandingDemo";
import ContactSalesDialog from "@/components/ContactSalesDialog";

/* =============================================================================
   BidForge Landing — Enterprise dark (Zinc/Chivo). Deliberately separate visual
   language from the in-app playful UI. See /app/design_guidelines.json.
   ============================================================================= */

const agentTiles = [
  { key: "discover",  Icon: Radar,     name: "Discover",  copy: "Discovery Agent scans SAM.gov continuously and ranks live opportunities by fit — bonding, service area, set-asides, NAICS." },
  { key: "qualify",   Icon: ScanLine,  name: "Qualify",   copy: "Bid/No-Bid Agent generates an executive brief, win probability, and go/no-go rationale before you touch the solicitation." },
  { key: "forecast",  Icon: LineChart, name: "Forecast",  copy: "Sources Sought, Presolicitation, Special Notice and J&A intel — position weeks before the RFP hits the street." },
  { key: "connect",   Icon: Users2,    name: "Connect",   copy: "Recent federal award winners from USAspending.gov with SAM POC enrichment and a 3-touch AI teaming cadence." },
  { key: "draft",     Icon: FileText,  name: "Draft",     copy: "Proposal Agent drafts 14-section responses, DOCX/PDF exports, construction submittal binders, and compliance matrices." },
];

const workflowSteps = [
  { n: "01", t: "Connect Your Profile",  d: "Upload capabilities, past performance, certifications, and rates. Your AI learns your business the same day." },
  { n: "02", t: "AI Finds & Qualifies",  d: "Every morning, review a pre-ranked pipeline with executive briefs and match scores already attached." },
  { n: "03", t: "Generate & Submit",     d: "Draft compliant, 14-section proposals in hours — reviewed by you, formatted to agency standard, ready to submit." },
];

const proofSignals = [
  { Icon: Cpu,       t: "Claude Sonnet 4.5",                sub: "Frontier-grade reasoning per response" },
  { Icon: Database,  t: "SAM.gov + USAspending Connected",  sub: "Live federal opportunity + award data" },
  { Icon: ShieldCheck, t: "Enterprise-grade Security",       sub: "TLS 1.3 · Encrypted at rest · Isolated tenants" },
  { Icon: Zap,       t: "24/7 Autonomy Agent",              sub: "Analyses run unattended, up to your daily cap" },
];

const faq = [
  { q: "Does AI actually write winning proposals?",
    a: "It writes the first 90%. BidForge drafts a fully compliant, 14-section response using your past performance and the RFP's Section L/M. You review, refine, and submit. Chief Construction won the Slate Creek Ranger Station roof job using a proposal drafted this way." },
  { q: "How does the credit system work?",
    a: "Every AI action consumes credits from your monthly allotment. A bid/no-bid brief is 1 credit; a full 14-section proposal is 3. Plans include 100 to 2,000 credits/month, and you can top up with add-on packs any time — every purchase is transparent, priced per credit." },
  { q: "Is our proprietary company data secure?",
    a: "Yes. Data is encrypted in transit and at rest. Every workspace lives in an isolated tenant scope. Nothing you upload is used to train third-party foundation models — your past performance stays yours." },
  { q: "How long does it take to learn the platform?",
    a: "The average operator ships their first AI-drafted proposal in under two hours. An onboarding tour walks you through profile setup, opportunity discovery, and one full proposal end-to-end." },
  { q: "Can it handle complex compliance matrices?",
    a: "Yes. The Compliance Agent cross-references Sections L, M, and clause-in-full-text-language callouts to produce a shall-statement matrix your proposal manager can audit line by line." },
  { q: "Can I add team members?",
    a: "Professional and Business plans include team seats. Invite BD managers, proposal writers, capture leads and SMEs — everyone shares one workspace, one pipeline, one activity feed." },
  { q: "Can I cancel any time?",
    a: "Any time. Downgrade or cancel from your billing page in a single click. Unused credit add-on packs never expire." },
];

// Sharp CTA styles — light-mode enterprise with high-contrast dark primary.
const primaryCTA = "inline-flex items-center justify-center gap-2 bg-zinc-900 text-white px-6 py-3 text-sm font-medium tracking-tight rounded-md transition-colors hover:bg-zinc-800 shadow-[0_1px_0_rgba(255,255,255,0.15)_inset]";
const secondaryCTA = "inline-flex items-center justify-center gap-2 bg-white border border-zinc-300 text-zinc-900 px-6 py-3 text-sm font-medium tracking-tight rounded-md transition-colors hover:border-zinc-400 hover:bg-zinc-50";
const eyebrow = "inline-block text-[11px] font-mono font-medium tracking-[0.2em] uppercase";
// Per-section eyebrow tints — colorful accents on an otherwise clean canvas.
const eyeIndigo  = `${eyebrow} text-indigo-600`;
const eyeEmerald = `${eyebrow} text-emerald-600`;
const eyeViolet  = `${eyebrow} text-violet-600`;
const eyeAmber   = `${eyebrow} text-amber-600`;
const eyeRose    = `${eyebrow} text-rose-600`;
const eyeSky     = `${eyebrow} text-sky-600`;
const eyeZinc    = `${eyebrow} text-zinc-500`;

/* Fake browser-chrome frame for the product-UI cards. */
function BrowserFrame({ children, className = "", label }) {
  return (
    <div className={`rounded-lg border border-zinc-200 bg-white shadow-[0_30px_60px_-20px_rgba(15,23,42,0.15)] overflow-hidden ${className}`}>
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-zinc-100 bg-zinc-50/60">
        <span className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
        <span className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
        <span className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
        {label && <span className="ml-3 text-[10px] font-mono text-zinc-500 tracking-widest">{label}</span>}
      </div>
      <div className="relative">{children}</div>
    </div>
  );
}

/* Real product screenshot inside the hero frame. */
function HeroProductMock() {
  return (
    <div className="bg-white">
      <img
        src="/product-screens/dashboard.jpg"
        alt="BidForge Command Center — live SAM.gov opportunity pipeline with match scores, deadline reminders, and proposal stage breakdown"
        className="block w-full h-auto"
        loading="eager"
        data-testid="hero-product-screenshot"
      />
    </div>
  );
}

/* Real Opportunities screenshot for the Solution row. */
function MiniOpportunityList() {
  return (
    <div className="bg-white">
      <img
        src="/product-screens/opportunities.jpg"
        alt="BidForge Opportunities — live SAM.gov feed with match score, agency, NAICS, and set-aside filters"
        className="block w-full h-auto"
        loading="lazy"
        data-testid="row-opportunities-screenshot"
      />
    </div>
  );
}

function MiniBidBrief() {
  return (
    <div className="p-5 bg-white">
      <div className="text-[10px] font-mono tracking-[0.2em] text-emerald-600 mb-4">BID / NO-BID · EXECUTIVE BRIEF</div>
      <div className="flex items-baseline justify-between mb-4">
        <div className="font-display text-4xl font-semibold text-emerald-600">GO</div>
        <div className="text-right">
          <div className="text-[10px] font-mono tracking-widest text-zinc-500">WIN PROBABILITY</div>
          <div className="font-display text-3xl text-zinc-900">72%</div>
        </div>
      </div>
      <div className="space-y-2.5 text-sm text-zinc-700 leading-relaxed">
        <div className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>Set-aside matches your certifications (SDVOSB)</span></div>
        <div className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>Past performance covers this scope (roofing, remote sites)</span></div>
        <div className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>Bonding capacity above threshold</span></div>
      </div>
    </div>
  );
}

function MiniProposalStack() {
  return (
    <div className="p-5 bg-white">
      <div className="text-[10px] font-mono tracking-[0.2em] text-violet-600 mb-4">PROPOSAL WORKSPACE · 14 SECTIONS</div>
      <div className="relative h-56">
        {[
          { top: "0", left: "0",  w: "82%", z: 3, title: "Technical Approach",  count: "1,847 words", tint: "bg-violet-50 border-violet-200" },
          { top: "18px", left: "24px", w: "82%", z: 2, title: "Management Plan",   count: "1,204 words", tint: "bg-white border-zinc-200" },
          { top: "36px", left: "48px", w: "82%", z: 1, title: "Past Performance",  count: "986 words",   tint: "bg-white border-zinc-200" },
        ].map((c, i) => (
          <div key={i} style={{ top: c.top, left: c.left, width: c.w, zIndex: c.z }} className={`absolute rounded-md border p-3 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.15)] ${c.tint}`}>
            <div className="text-[10px] font-mono tracking-widest text-zinc-500 mb-1">SECTION</div>
            <div className="text-sm text-zinc-900 font-medium">{c.title}</div>
            <div className="text-[10px] font-mono text-emerald-600 mt-1">✓ COMPLIANT · {c.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniConnect() {
  return (
    <div className="bg-white">
      <img
        src="/product-screens/connect.jpg"
        alt="BidForge Connect — recent federal award winners from USAspending.gov with SAM.gov POC enrichment and a 3-touch AI teaming cadence"
        className="block w-full h-auto"
        loading="lazy"
        data-testid="row-connect-screenshot"
      />
    </div>
  );
}

function FaqItem({ q, a, i, open, onToggle }) {
  return (
    <div className="border-b border-zinc-200">
      <button onClick={onToggle} className="w-full flex items-start justify-between gap-6 py-6 text-left group" data-testid={`faq-${i}`}>
        <span className="text-lg text-zinc-900 font-medium tracking-tight group-hover:text-indigo-600 transition-colors">{q}</span>
        <span className="mt-1 text-zinc-400 group-hover:text-indigo-600 transition-colors shrink-0">
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      {open && <div className="pb-6 text-zinc-600 leading-relaxed max-w-3xl">{a}</div>}
    </div>
  );
}

export default function Landing() {
  const [openFaq, setOpenFaq] = useState(0);
  const [demoOpen, setDemoOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  const containerRef = useRef(null);

  // Section-entry fade-up
  useEffect(() => {
    const els = containerRef.current?.querySelectorAll("[data-anim]");
    if (!els?.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in-view"); obs.unobserve(e.target); } });
    }, { threshold: 0.15 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="min-h-screen bg-stone-50 text-zinc-900 antialiased" style={{ fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="landing-page">
      <style>{`
        [data-anim] { opacity: 0; transform: translateY(20px); transition: opacity .7s ease, transform .7s ease; }
        [data-anim].in-view { opacity: 1; transform: none; }
        .bf-display { font-family: 'Chivo', sans-serif; letter-spacing: -0.02em; }
        .bf-grain { position: relative; }
        .bf-grain::before { content: ""; position: absolute; inset: 0; pointer-events: none; opacity: 0.03;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' /></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>"); }
      `}</style>

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-zinc-200 bg-stone-50/85 backdrop-blur-xl">
        <div className="max-w-[1280px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <Logo className="w-7 h-7" />
            <span className="bf-display font-bold text-lg tracking-tight text-zinc-900">BidForge</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-600">
            <a href="#solution" className="hover:text-zinc-900 transition-colors" data-testid="nav-solution">Platform</a>
            <a href="#process" className="hover:text-zinc-900 transition-colors" data-testid="nav-process">How it works</a>
            <a href="#pricing" className="hover:text-zinc-900 transition-colors" data-testid="nav-pricing-anchor">Pricing</a>
            <a href="#faq" className="hover:text-zinc-900 transition-colors" data-testid="nav-faq">FAQ</a>
            <Link to="/pricing" className="hover:text-zinc-900 transition-colors" data-testid="nav-pricing">Full plans</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-zinc-700 hover:text-zinc-900 transition-colors" data-testid="nav-login">Log in</Link>
            <Link to="/signup" className={primaryCTA + " !py-2 !px-4 !text-xs"} data-testid="nav-cta">
              Start free <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* 1) HERO */}
      <section className="relative overflow-hidden bf-grain">
        <div aria-hidden className="absolute inset-x-0 top-0 h-[500px] pointer-events-none" style={{ background: "radial-gradient(ellipse 70% 60% at 30% 0%, rgba(99,102,241,0.10), transparent 60%), radial-gradient(ellipse 60% 50% at 90% 20%, rgba(236,72,153,0.08), transparent 60%)" }} />
        <div className="max-w-[1280px] mx-auto px-6 pt-20 pb-8 relative">
          <div className="max-w-4xl" data-anim>
            <span className={eyeIndigo}>Government Contracting AI</span>
            <h1 className="mt-6 bf-display font-bold text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-zinc-900">
              Find the right government bids.
              <br />
              <span className="text-zinc-500">Decide faster.</span>
              <br />
              Generate proposals with <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">AI</span>.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-zinc-600 max-w-2xl leading-relaxed">
              The unified bid &amp; proposal platform for government contractors. Discover opportunities on SAM.gov, qualify them instantly, and draft compliant, section-by-section proposals — with autonomous AI agents that do the work.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/signup" className={primaryCTA} data-testid="hero-primary-cta">Start your free trial <ArrowRight className="w-4 h-4" /></Link>
              <button onClick={() => setDemoOpen(true)} className={secondaryCTA} data-testid="hero-demo-cta"><PlayCircle className="w-4 h-4" />Watch 60-sec demo</button>
              <a href="#solution" className={secondaryCTA} data-testid="hero-secondary-cta">See the platform <ChevronRight className="w-4 h-4" /></a>
              <div className="text-xs text-zinc-500 ml-2">7-day free trial · No card required</div>
            </div>
          </div>

          {/* Hero product frame */}
          <div className="mt-16 relative" data-anim>
            <BrowserFrame label="bidforgeai.org/dashboard" className="mx-auto max-w-5xl">
              <HeroProductMock />
            </BrowserFrame>
            {/* Floating overlay */}
            <div className="hidden lg:block absolute right-0 -bottom-6 rounded-lg border border-emerald-200 bg-white backdrop-blur px-4 py-3 shadow-2xl">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-[10px] font-mono tracking-widest text-zinc-500">MATCH SCORE</span>
              </div>
              <div className="bf-display text-2xl font-semibold text-emerald-600">98%</div>
              <div className="text-[10px] text-zinc-500">Slate Creek · USDA</div>
            </div>
          </div>
        </div>

        {/* Trust bar */}
        <div className="max-w-[1280px] mx-auto px-6 pb-20 pt-24 border-t border-zinc-200 mt-16">
          <div className="text-[10px] font-mono tracking-[0.25em] text-zinc-500 uppercase text-center mb-6">Integrated with U.S. federal data infrastructure</div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-zinc-500">
            {["SAM.gov", "USAspending.gov", "FPDS", "GSA eBuy", "FedConnect"].map((n) => (
              <span key={n} className="text-sm font-medium tracking-tight opacity-70 hover:opacity-100 transition-opacity">{n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* 2) PROBLEM */}
      <section className="border-t border-zinc-200 py-24 md:py-32 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 grid lg:grid-cols-2 gap-16" data-anim>
          <div>
            <span className={eyeRose}>The old way is broken</span>
            <h2 className="mt-6 bf-display font-bold text-4xl md:text-5xl tracking-tight text-zinc-900 leading-[1.05]">Fragmented workflows cost contractors real contracts.</h2>
          </div>
          <div className="space-y-8">
            {[
              { t: "Disconnected tools", d: "Jumping between SAM.gov, USAspending, spreadsheets, and scattered Word docs — a full-time job before you've bid on anything." },
              { t: "Wasted B&P budget", d: "Manual qualification means chasing bad-fit opportunities. Every hour on the wrong RFP is an hour off the right one." },
              { t: "Weeks lost to drafting", d: "Small teams take 20–40 hours to produce a single compliant proposal. Larger primes staff whole departments to move at that pace." },
              { t: "Missed teaming windows", d: "By the time you learn a prime just won a $50M task order, they've already assembled their sub roster." },
            ].map((p, i) => (
              <div key={p.t} className="flex gap-5">
                <div className="text-[10px] font-mono text-rose-500 tracking-widest pt-1.5">0{i + 1}</div>
                <div className="flex-1 border-b border-zinc-200 pb-8 last:border-b-0">
                  <div className="text-lg font-medium text-zinc-900 tracking-tight">{p.t}</div>
                  <div className="mt-2 text-zinc-600 leading-relaxed">{p.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3) SOLUTION */}
      <section id="solution" className="border-t border-zinc-200 py-24 md:py-32 relative bf-grain" style={{ background: "linear-gradient(180deg, #FAFAF9 0%, #F5F3FF 50%, #FAFAF9 100%)" }}>
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="max-w-3xl" data-anim>
            <span className={eyeViolet}>Your AI proposal factory</span>
            <h2 className="mt-6 bf-display font-bold text-4xl md:text-5xl tracking-tight text-zinc-900 leading-[1.05]">One platform, five agents, every stage of the federal cycle.</h2>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed">Meet the autonomous team working the pipeline while you focus on winning. Each agent is purpose-built for one stage of the government-contracting lifecycle.</p>
          </div>

          {/* Agent tiles */}
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-5 gap-4" data-anim>
            {agentTiles.map((a, idx) => {
              const Icon = a.Icon;
              const tints = [
                "bg-indigo-50 text-indigo-600 border-indigo-200",
                "bg-emerald-50 text-emerald-600 border-emerald-200",
                "bg-sky-50 text-sky-600 border-sky-200",
                "bg-amber-50 text-amber-600 border-amber-200",
                "bg-violet-50 text-violet-600 border-violet-200",
              ];
              return (
                <div key={a.key} className="group rounded-lg border border-zinc-200 bg-white p-5 hover:border-zinc-300 hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`agent-tile-${a.key}`}>
                  <div className={`w-9 h-9 rounded-md border flex items-center justify-center ${tints[idx]}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div className="mt-5 text-base font-medium text-zinc-900 tracking-tight">{a.name}</div>
                  <div className="mt-2 text-sm text-zinc-600 leading-relaxed">{a.copy}</div>
                </div>
              );
            })}
          </div>

          {/* Workflow rows with screenshots */}
          <div className="mt-24 space-y-24">
            {[
              { side: "left",  Icon: Radar,     tint: "text-indigo-600 border-indigo-200 bg-indigo-50", name: "01 / DISCOVER", head: "Never miss a fit-for-purpose opportunity.", body: "The Discovery Agent scans SAM.gov continuously, matches every posting against your NAICS, set-asides, service area, and bonding capacity, and ranks it 0-100 so you know exactly what to open first.", Frame: MiniOpportunityList, label: "app.bidforgeai.org/opportunities" },
              { side: "right", Icon: ScanLine,  tint: "text-emerald-600 border-emerald-200 bg-emerald-50", name: "02 / QUALIFY",  head: "A go/no-go decision, before you read the RFP.", body: "Bid/No-Bid Agent produces an executive brief in seconds — win probability, capability match, competitive landscape, and clear rationale. Move on the yes, walk away from the no.", Frame: MiniBidBrief, label: "opportunity/1240LU25Q0094" },
              { side: "left",  Icon: FileText,  tint: "text-violet-600 border-violet-200 bg-violet-50",   name: "03 / DRAFT",    head: "Full 14-section proposals in hours, not weeks.", body: "Proposal Agent drafts every section of your response in parallel, cross-references Section L/M, exports to DOCX/PDF, and — for construction — assembles a Slate-Creek-style Submittals Binder that meets agency formatting.", Frame: MiniProposalStack, label: "proposal/workspace" },
              { side: "right", Icon: Users2,    tint: "text-amber-600 border-amber-200 bg-amber-50",     name: "04 / CONNECT",  head: "Team with the primes who just won.", body: "Pull recent federal awards from USAspending.gov, enrich each prime with SAM.gov POC data, and let the AI cadence run a professional 3-touch outreach sequence — automatically armed the moment you mark a prime 'contacted'.", Frame: MiniConnect, label: "app.bidforgeai.org/connect" },
            ].map((row) => (
              <div key={row.name} className={`grid lg:grid-cols-2 gap-12 items-center ${row.side === "right" ? "lg:[&>*:first-child]:order-2" : ""}`} data-anim>
                <div>
                  <BrowserFrame label={row.label}><row.Frame /></BrowserFrame>
                </div>
                <div className="lg:pl-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-8 h-8 rounded-md border flex items-center justify-center ${row.tint}`}><row.Icon className="w-4 h-4" strokeWidth={1.5} /></div>
                    <span className={`${eyebrow} ${row.tint.split(" ")[0]}`}>{row.name}</span>
                  </div>
                  <h3 className="bf-display font-bold text-3xl md:text-4xl text-zinc-900 tracking-tight leading-[1.1]">{row.head}</h3>
                  <p className="mt-5 text-zinc-600 leading-relaxed text-lg">{row.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="border-t border-zinc-200 py-24 md:py-32 bg-white">
        <div className="max-w-[1280px] mx-auto px-6" data-anim>
          <div className="max-w-2xl mb-14">
            <span className={eyeSky}>Who it's for</span>
            <h2 className="mt-6 bf-display font-bold text-4xl md:text-5xl tracking-tight text-zinc-900">Built for both sides of the federal market.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50/70 to-white p-8" data-testid="audience-smb">
              <div className={eyeIndigo}>Small business &amp; set-asides</div>
              <h3 className="mt-4 bf-display text-2xl font-semibold text-zinc-900 tracking-tight">HUBZone. 8(a). SDVOSB. WOSB.</h3>
              <p className="mt-3 text-zinc-600 leading-relaxed">Punch above your weight class. Compete with mid-primes without hiring a proposal shop.</p>
              <ul className="mt-6 space-y-3 text-sm text-zinc-700">
                {["Virtual proposal team on every plan","Auto-qualify only the RFPs that fit your certs","Estimating Agent produces priceable WBS scope","Submittals Binder for construction contracts"].map((b) => (
                  <li key={b} className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{b}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50/70 to-white p-8" data-testid="audience-prime">
              <div className={`${eyebrow} text-fuchsia-600`}>Mid-size primes &amp; capture teams</div>
              <h3 className="mt-4 bf-display text-2xl font-semibold text-zinc-900 tracking-tight">Industrialize your BD pipeline.</h3>
              <p className="mt-3 text-zinc-600 leading-relaxed">Scale volume without hiring more capture managers. Ship 10x more compliant proposals per quarter.</p>
              <ul className="mt-6 space-y-3 text-sm text-zinc-700">
                {["Autonomy Agent runs 24/7 up to a daily cap","Team seats, roles, and shared knowledge base","Forecaster surfaces Sources Sought before RFPs drop","Connect finds prime-of-primes teaming opportunities"].map((b) => (
                  <li key={b} className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{b}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 4) HOW IT WORKS */}
      <section id="process" className="border-t border-zinc-200 py-24 md:py-32" style={{ background: "linear-gradient(180deg, #FAFAF9 0%, #FEF3C7 50%, #FAFAF9 100%)" }}>
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="max-w-2xl mb-14" data-anim>
            <span className={eyeAmber}>Process</span>
            <h2 className="mt-6 bf-display font-bold text-4xl md:text-5xl tracking-tight text-zinc-900 leading-[1.05]">From solicitation to submission in three steps.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6" data-anim>
            {workflowSteps.map((s, i) => {
              const stepTints = [
                "border-indigo-200 from-indigo-50/60",
                "border-emerald-200 from-emerald-50/60",
                "border-violet-200 from-violet-50/60",
              ];
              const numColors = ["text-indigo-600", "text-emerald-600", "text-violet-600"];
              return (
                <div key={s.n} className={`rounded-lg border ${stepTints[i]} bg-gradient-to-br to-white p-8`} data-testid={`step-${s.n}`}>
                  <div className={`${eyebrow} ${numColors[i]}`}>Step {s.n}</div>
                  <h3 className="mt-4 bf-display text-2xl font-semibold text-zinc-900 tracking-tight">{s.t}</h3>
                  <p className="mt-3 text-zinc-600 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5) PROOF & TRUST */}
      <section className="border-t border-zinc-200 py-24 md:py-32 relative bf-grain bg-white">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="max-w-2xl mb-14" data-anim>
            <span className={eyeEmerald}>Enterprise grade</span>
            <h2 className="mt-6 bf-display font-bold text-4xl md:text-5xl tracking-tight text-zinc-900 leading-[1.05]">Built for mission-critical contracting.</h2>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed">Live in production. Live opportunities. Live federal data.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-16" data-anim>
            {proofSignals.map((p, idx) => {
              const iconTints = ["text-indigo-600", "text-emerald-600", "text-violet-600", "text-amber-600"];
              return (
                <div key={p.t} className="rounded-lg border border-zinc-200 bg-white p-6 hover:shadow-lg transition-shadow">
                  <p.Icon className={`w-5 h-5 ${iconTints[idx]}`} strokeWidth={1.5} />
                  <div className="mt-4 text-sm font-medium text-zinc-900 tracking-tight">{p.t}</div>
                  <div className="mt-1 text-xs text-zinc-500">{p.sub}</div>
                </div>
              );
            })}
          </div>
          {/* Case-study strip */}
          <div className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50/70 via-white to-white p-10" data-anim>
            <div className="grid lg:grid-cols-3 gap-10 items-start">
              <div className="lg:col-span-2">
                <div className={eyeEmerald}>Field-tested</div>
                <h3 className="mt-4 bf-display text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight leading-tight">
                  "The Slate Creek Ranger Station roof-replacement package was drafted end-to-end by BidForge — and we won."
                </h3>
                <div className="mt-4 text-sm text-zinc-500">— Chief Construction Co · USDA Forest Service · 2025</div>
              </div>
              <div className="grid grid-cols-3 gap-4 lg:pl-6 lg:border-l lg:border-zinc-200">
                {[{ n: "hours", v: "3" }, { n: "sections", v: "14" }, { n: "compliance", v: "100%" }].map((k) => (
                  <div key={k.n}>
                    <div className="bf-display text-3xl font-semibold text-emerald-600">{k.v}</div>
                    <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase mt-1">{k.n}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6) PRICING PREVIEW */}
      <section id="pricing" className="border-t border-zinc-200 py-24 md:py-32 bg-white">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="max-w-2xl mb-14" data-anim>
            <span className={eyeViolet}>Pricing</span>
            <h2 className="mt-6 bf-display font-bold text-4xl md:text-5xl tracking-tight text-zinc-900 leading-[1.05]">
              Priced for the department it <span className="text-violet-600">replaces</span>.
            </h2>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed">
              Four tiers. One workspace. Every plan includes AI opportunity discovery, proposals, and compliance sweeps. Annual billing saves 20%.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-anim>
            {[
              { name: "Starter",      tag: "You bid",           price: "$99",  cap: "10 proposals/mo",     seats: "1 seat · up to 3",  credits: "100 credits/mo",  tone: "sky",     over: "$25/proposal over cap" },
              { name: "Professional", tag: "Your team bids",     price: "$299", cap: "40 proposals/mo",     seats: "3 seats · up to 5",  credits: "500 credits/mo",  tone: "violet",  over: "$15/proposal over cap", featured: true },
              { name: "Business",     tag: "BidForge bids",      price: "$599", cap: "Unlimited proposals", seats: "5 seats · up to 10", credits: "2,000 credits/mo", tone: "fuchsia", over: "Autonomous Agent + API" },
              { name: "Enterprise",   tag: "Your division bids", price: "Custom", cap: "Unlimited",         seats: "Unlimited seats",    credits: "Unlimited credits", tone: "amber",   over: "SSO · Private deploy · SLA" },
            ].map((t) => {
              const tints = {
                sky:     "border-sky-200 bg-sky-50/40",
                violet:  "border-violet-300 ring-2 ring-violet-200/40 shadow-[0_20px_60px_-15px_rgba(139,92,246,0.35)] bg-white",
                fuchsia: "border-fuchsia-200 bg-fuchsia-50/40",
                amber:   "border-amber-200 bg-amber-50/40",
              };
              const eyeTints = { sky: "text-sky-600", violet: "text-violet-600", fuchsia: "text-fuchsia-600", amber: "text-amber-600" };
              return (
                <div key={t.name} className={`rounded-lg border p-6 flex flex-col ${tints[t.tone]}`} data-testid={`landing-tier-${t.name.toLowerCase()}`}>
                  {t.featured
                    ? <div className="inline-flex self-start rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[10px] font-mono font-bold tracking-widest px-3 py-1 mb-4">MOST POPULAR</div>
                    : <div className={`text-[10px] font-mono font-bold tracking-widest ${eyeTints[t.tone]} mb-4 uppercase`}>{t.tag}</div>}
                  <div className="bf-display text-2xl font-semibold text-zinc-900 tracking-tight">{t.name}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <div className="bf-display text-4xl font-bold text-zinc-900">{t.price}</div>
                    {t.price !== "Custom" && <div className="text-sm text-zinc-500">/mo</div>}
                  </div>
                  <div className="mt-5 space-y-2 text-sm text-zinc-700 flex-1">
                    <div className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span className="font-medium">{t.cap}</span></div>
                    <div className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>{t.seats}</span></div>
                    <div className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span>{t.credits}</span></div>
                    <div className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span className="text-zinc-500">{t.over}</span></div>
                  </div>
                  <Link
                    to={t.name === "Enterprise" ? "#" : "/signup"}
                    onClick={t.name === "Enterprise" ? (e) => { e.preventDefault(); setSalesOpen(true); } : undefined}
                    className={`mt-6 text-center py-2.5 rounded-md text-xs font-mono font-bold tracking-widest transition-all ${t.featured ? "bg-zinc-900 text-white hover:bg-zinc-800" : "border border-zinc-200 text-zinc-800 hover:border-zinc-400"}`}
                    data-testid={`landing-cta-${t.name.toLowerCase()}`}
                  >{t.name === "Enterprise" ? "CONTACT SALES" : "START FREE TRIAL"}</Link>
                </div>
              );
            })}
          </div>
          <div className="mt-10 text-center text-sm text-zinc-500">
            <Link to="/pricing" className="text-violet-600 hover:text-violet-700 underline underline-offset-2 font-medium" data-testid="landing-pricing-link">See full comparison & annual pricing →</Link>
          </div>
        </div>
      </section>

      {/* 7) FAQ */}
      <section id="faq" className="border-t border-zinc-200 py-24 md:py-32 bg-stone-50">
        <div className="max-w-[1280px] mx-auto px-6 grid lg:grid-cols-[380px,1fr] gap-16">
          <div data-anim>
            <span className={eyeIndigo}>FAQ</span>
            <h2 className="mt-6 bf-display font-bold text-4xl md:text-5xl tracking-tight text-zinc-900 leading-[1.05]">Answers to the toughest questions.</h2>
            <p className="mt-6 text-zinc-600 leading-relaxed">Not seeing yours? <a href="mailto:hello@bidforgeai.org" className="text-indigo-600 underline underline-offset-4 hover:text-indigo-700 transition-colors" data-testid="faq-contact">Email us.</a></p>
          </div>
          <div data-anim>
            {faq.map((f, i) => (
              <FaqItem key={f.q} i={i} q={f.q} a={f.a} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* 7) FINAL CTA */}
      <section className="border-t border-zinc-200 py-24 md:py-32 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #EEF2FF 50%, #FDF2F8 100%)" }}>
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 50% 60% at 50% 100%, rgba(139,92,246,0.10), transparent 60%)" }} />
        <div className="max-w-[1280px] mx-auto px-6 text-center relative" data-anim>
          <span className={eyeViolet}>Start winning</span>
          <h2 className="mt-6 bf-display font-bold text-4xl md:text-6xl tracking-tight text-zinc-900 leading-[1.02] max-w-4xl mx-auto">Industrialize your BD process today.</h2>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl mx-auto leading-relaxed">
            Join the modern government contractors scaling federal revenue with BidForge. Plans start at $99/mo (100 credits). Cancel anytime.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/signup" className={primaryCTA} data-testid="footer-primary-cta">Start your free trial <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/pricing" className={secondaryCTA} data-testid="footer-pricing-cta">See pricing</Link>
          </div>
          <div className="mt-6 text-xs text-zinc-500">7-day free trial · No card required · Cancel any time</div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white py-14">
        <div className="max-w-[1280px] mx-auto px-6 grid md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Logo className="w-7 h-7" />
              <span className="bf-display font-bold text-lg tracking-tight text-zinc-900">BidForge</span>
            </div>
            <p className="text-sm text-zinc-500 leading-relaxed max-w-xs">The AI operating system for federal contractors.</p>
          </div>
          <div>
            <div className="text-xs font-mono tracking-widest text-zinc-500 uppercase mb-4">Platform</div>
            <ul className="space-y-2 text-sm text-zinc-600">
              <li><a href="#solution" className="hover:text-zinc-900">Agents</a></li>
              <li><a href="#process" className="hover:text-zinc-900">How it works</a></li>
              <li><Link to="/pricing" className="hover:text-zinc-900">Pricing</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-mono tracking-widest text-zinc-500 uppercase mb-4">Company</div>
            <ul className="space-y-2 text-sm text-zinc-600">
              <li><a href="#faq" className="hover:text-zinc-900">FAQ</a></li>
              <li><a href="mailto:hello@bidforgeai.org" className="hover:text-zinc-900">Contact</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-mono tracking-widest text-zinc-500 uppercase mb-4">Get started</div>
            <Link to="/signup" className={primaryCTA + " !py-2 !px-4 !text-xs"} data-testid="footer-signup">Start free <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
        </div>
        <div className="max-w-[1280px] mx-auto px-6 mt-12 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
          <div>© 2026 BidForge. All rights reserved.</div>
          <div className="font-mono tracking-widest">bidforgeai.org</div>
        </div>
      </footer>

      <LandingDemo open={demoOpen} onClose={() => setDemoOpen(false)} />
      <ContactSalesDialog open={salesOpen} onClose={() => setSalesOpen(false)} context="Requesting Enterprise plan pricing and demo." />
    </div>
  );
}
